/**
 * QuoteResponseDialog
 * Dialog for contractors/verifiers to respond to a quote request with pricing.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  InputAdornment,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox
} from '@mui/material';
import {
  Send,
  CheckCircle,
  AttachMoney,
  CalendarToday,
  Schedule,
  LocationOn
} from '@mui/icons-material';
import api from '../../services/api';
import DialogCloseButton from '../common/DialogCloseButton';
import { harareInputToIso } from '../../utils/time';

const QuoteResponseDialog = ({ open, onClose, quoteRequest, onSuccess }) => {
  const [quoteAmount, setQuoteAmount] = useState('');
  const [currency] = useState('USD');
  const [proposedStartDate, setProposedStartDate] = useState('');
  const [estimatedTimelineDays, setEstimatedTimelineDays] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [needsSiteVisit, setNeedsSiteVisit] = useState(false);
  const [visitType, setVisitType] = useState('in_person'); // site visit or client call
  const [visitFeeAmount, setVisitFeeAmount] = useState('');
  const [visitFeeDescription, setVisitFeeDescription] = useState('');
  const feeLocked = ['held', 'released'].includes(quoteRequest?.visit_fee_status);
  const emptyWindows = () => [{ start: '', end: '' }, { start: '', end: '' }, { start: '', end: '' }];
  const [visitWindows, setVisitWindows] = useState(emptyWindows());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Revising an already-priced response: start from the current price so the
  // provider edits rather than retypes (or ticks "site visit" to withdraw it).
  useEffect(() => {
    if (open && quoteRequest?.quote_amount) {
      setQuoteAmount(String(quoteRequest.quote_amount));
    }
    // Revising with a previously proposed (unpaid) fee: start from it.
    if (open && quoteRequest?.visit_fee_amount && quoteRequest?.visit_fee_status === 'proposed') {
      setVisitFeeAmount(String(quoteRequest.visit_fee_amount));
      setVisitFeeDescription(quoteRequest.visit_fee_description || '');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    // Each entry is an exact start time, or a { start, end } window when an
    // end time was given ("any time between 8 and 12").
    const proposedWindows = visitWindows
      .filter((w) => w.start)
      .map((w) => (w.end
        ? { start: harareInputToIso(w.start), end: harareInputToIso(w.end) }
        : harareInputToIso(w.start)));
    if (needsSiteVisit) {
      if (proposedWindows.length === 0 && !responseMessage.trim()) {
        setError('Propose at least one visit time, or add a message explaining what you need.');
        return;
      }
    } else if (!quoteAmount || parseFloat(quoteAmount) <= 0) {
      setError('Please enter a valid quote amount');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.put(`/quote-requests/${quoteRequest.id}/respond`, {
        quoteAmount: needsSiteVisit ? null : parseFloat(quoteAmount),
        currency,
        proposedStartDate: (!needsSiteVisit && proposedStartDate) || null,
        estimatedTimelineDays: (!needsSiteVisit && estimatedTimelineDays) ? parseInt(estimatedTimelineDays) : null,
        responseMessage: responseMessage || null,
        proposedVisitWindows: needsSiteVisit ? proposedWindows : [],
        visitType: needsSiteVisit ? visitType : undefined,
        visitFeeAmount: (!feeLocked && needsSiteVisit && visitFeeAmount) ? parseFloat(visitFeeAmount) : undefined,
        visitFeeDescription: (!feeLocked && needsSiteVisit && visitFeeAmount && visitFeeDescription.trim()) ? visitFeeDescription.trim() : undefined
      });

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          if (onSuccess) onSuccess(response.data.data);
          handleClose();
        }, 1500);
      } else {
        setError(response.data.message || 'Failed to send response');
      }
    } catch (err) {
      console.error('Quote response error:', err);
      setError(err.response?.data?.message || 'Failed to send quote response. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setQuoteAmount('');
      setProposedStartDate('');
      setEstimatedTimelineDays('');
      setResponseMessage('');
      setVisitFeeAmount('');
      setVisitFeeDescription('');
      setNeedsSiteVisit(false);
      setVisitWindows(emptyWindows());
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  if (!quoteRequest) return null;

  const cleanDisplayName = (value) => {
    if (!value || typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return null;
    return trimmed;
  };

  const clientDisplayName = cleanDisplayName(quoteRequest.client_display_name)
    || cleanDisplayName(quoteRequest.client_name)
    || 'Client';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogCloseButton onClick={handleClose} sx={{ color: 'white' }} />
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #0d9488 0%, #134e4a 100%)',
          color: 'white',
          py: 3,
          px: 3
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" fontWeight={700}>
              <AttachMoney sx={{ mr: 1, verticalAlign: 'middle' }} />
              Respond to Quote Request
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
              Provide your pricing and timeline
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {success && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>Quote Response Sent!</Typography>
            <Typography variant="body2" color="text.secondary">
              The client will be notified of your response.
            </Typography>
          </Box>
        )}

        {error && !success && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!success && (
          <>
            {/* Original Request Details */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 3,
                bgcolor: '#f8f9fa',
                border: '1px solid #e0e0e0',
                borderRadius: 2
              }}
            >
              <Typography variant="overline" color="text.secondary" gutterBottom>
                Quote Request Details
              </Typography>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 0.5, mb: 1 }}>
                From: {clientDisplayName}
              </Typography>

              <Typography variant="body2" sx={{ mb: 1 }}>
                {quoteRequest.project_description}
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {quoteRequest.project_location && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocationOn fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {quoteRequest.project_location}
                    </Typography>
                  </Box>
                )}
                {quoteRequest.estimated_budget && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AttachMoney fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Client budget: ${parseFloat(quoteRequest.estimated_budget).toFixed(2)}
                    </Typography>
                  </Box>
                )}
                {quoteRequest.timeline && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Schedule fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {quoteRequest.timeline}
                    </Typography>
                  </Box>
                )}
              </Box>

              {quoteRequest.additional_notes && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Additional Notes:</Typography>
                  <Typography variant="body2">{quoteRequest.additional_notes}</Typography>
                </Box>
              )}
            </Paper>

            <Divider sx={{ my: 2 }} />

            {/* Response Form */}
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Your Quote
            </Typography>

            {/* Free-site-visit policy: this is why the amount is optional. */}
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Site visits are free for project owners.</strong> Never charge a fee to
              visit or assess a project. If you need to see the site before you can price the
              work, tick the box below and propose visit times instead.
            </Alert>

            <FormControlLabel
              sx={{ mb: 1 }}
              control={
                <Checkbox
                  checked={needsSiteVisit}
                  onChange={(e) => setNeedsSiteVisit(e.target.checked)}
                />
              }
              label="I need a site visit before I can give a price"
            />

            {needsSiteVisit ? (
              <>
                {/* A time slot can be a physical site visit or a phone/video call */}
                <RadioGroup
                  row
                  value={visitType}
                  onChange={(e) => setVisitType(e.target.value)}
                  sx={{ mb: 1 }}
                >
                  <FormControlLabel value="in_person" control={<Radio size="small" />} label="Site visit" />
                  <FormControlLabel value="client_call" control={<Radio size="small" />} label="Phone / video call" />
                </RadioGroup>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Propose up to three dates/times, in Zimbabwe (Harare / CAT)
                  time. Add an end time to offer a window (e.g. "any time between 8am and
                  midday") instead of an exact time. {visitType === 'in_person'
                    ? 'The owner confirms one and will share access details (who will meet you, gate/key info) with the confirmed time.'
                    : 'The owner confirms one and you call them at the agreed time.'}
                </Typography>
                {/* Optional call-out fee — paid through Eqaya into escrow,
                    never cash on the day. Locked once the client has paid. */}
                {feeLocked ? (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Call-out fee of ${Number(quoteRequest.visit_fee_amount).toFixed(2)} is{' '}
                    {quoteRequest.visit_fee_status === 'released' ? 'released to your earnings' : 'held in escrow'} — it can no longer be changed.
                  </Alert>
                ) : (
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <TextField
                      label="Call-out fee (USD, optional)"
                      type="number"
                      value={visitFeeAmount}
                      onChange={(e) => setVisitFeeAmount(e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      placeholder="e.g. 20"
                      helperText="Charged for the visit itself. The owner pays it through Eqaya — it's held in escrow and released to you once the visit is confirmed complete. Never ask for cash on the day."
                      sx={{ minWidth: 220, flex: 1 }}
                    />
                    <TextField
                      label="What the fee covers"
                      value={visitFeeDescription}
                      onChange={(e) => setVisitFeeDescription(e.target.value.slice(0, 200))}
                      placeholder="e.g. kitchen design work"
                      disabled={!visitFeeAmount}
                      sx={{ minWidth: 220, flex: 1 }}
                    />
                  </Box>
                )}
                {visitWindows.map((windowValue, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <TextField
                      label={`Option ${index + 1}`}
                      type="datetime-local"
                      value={windowValue.start}
                      onChange={(e) => {
                        const next = [...visitWindows];
                        next[index] = { ...next[index], start: e.target.value };
                        setVisitWindows(next);
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 220, flex: 1 }}
                    />
                    <TextField
                      label="Until (optional)"
                      type="datetime-local"
                      value={windowValue.end}
                      onChange={(e) => {
                        const next = [...visitWindows];
                        next[index] = { ...next[index], end: e.target.value };
                        setVisitWindows(next);
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 220, flex: 1 }}
                    />
                  </Box>
                ))}
              </>
            ) : (
              <>
                {/* Quote Amount */}
                <TextField
                  fullWidth
                  label="Quote Amount (USD) *"
                  type="number"
                  value={quoteAmount}
                  onChange={(e) => setQuoteAmount(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                  placeholder="Enter your price"
                  sx={{ mb: 2 }}
                />

                {/* Start Date & Timeline side by side */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Proposed Start Date"
                    type="date"
                    value={proposedStartDate}
                    onChange={(e) => setProposedStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><CalendarToday fontSize="small" /></InputAdornment>
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Estimated Timeline (Days)"
                    type="number"
                    value={estimatedTimelineDays}
                    onChange={(e) => setEstimatedTimelineDays(e.target.value)}
                    placeholder="e.g., 30"
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Schedule fontSize="small" /></InputAdornment>
                    }}
                  />
                </Box>
              </>
            )}

            {/* Response Message */}
            <TextField
              fullWidth
              multiline
              rows={4}
              label={needsSiteVisit ? 'Message to the owner (what you need and why)' : 'Message (Optional)'}
              value={responseMessage}
              onChange={(e) => setResponseMessage(e.target.value)}
              placeholder={needsSiteVisit
                ? 'e.g. I need to assess the roof structure and measurements on site before I can give a responsible fixed price.'
                : 'Add any additional details about your quote, approach, or questions for the client...'}
              helperText="Phone numbers and emails are removed automatically — all communication stays on Eqaya."
            />
          </>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || (!needsSiteVisit && !quoteAmount)}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
            sx={{
              background: 'linear-gradient(135deg, #0d9488 0%, #134e4a 100%)',
              color: 'white',
              fontWeight: 600,
              px: 3
            }}
          >
            {needsSiteVisit ? 'Request Site Visit' : 'Send Quote Response'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default QuoteResponseDialog;
